export type CallSheetDigestStatus =
  | 'sent'
  | 'skipped-no-calls'
  | 'skipped-no-recipients'
  | 'failed';

export interface CallSheetDigestSettings {
  enabled: boolean;
  sendTime: string;
  lastRunLocalDate?: string;
  lastRunAt?: string;
  lastSentAt?: string;
  lastStatus?: CallSheetDigestStatus;
}

export interface ProjectCommunicationSettings {
  timeZone: string;
  callSheetDigest: CallSheetDigestSettings;
}

export const DEFAULT_COMMUNICATION_SETTINGS: ProjectCommunicationSettings = {
  timeZone: 'America/New_York',
  callSheetDigest: {
    enabled: false,
    sendTime: '20:00',
  },
};

const VALID_DIGEST_STATUSES = new Set<CallSheetDigestStatus>([
  'sent',
  'skipped-no-calls',
  'skipped-no-recipients',
  'failed',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function normalizeTime(value: unknown): string {
  if (typeof value !== 'string') {
    return DEFAULT_COMMUNICATION_SETTINGS.callSheetDigest.sendTime;
  }

  const normalized = value.trim().toLowerCase().replace(/\s+/g, '');
  const match = normalized.match(/^(\d{1,2})(?::(\d{2}))?(am|pm)?$/);
  if (!match) {
    return DEFAULT_COMMUNICATION_SETTINGS.callSheetDigest.sendTime;
  }

  const hourValue = Number(match[1]);
  const minuteValue = match[2] === undefined ? 0 : Number(match[2]);
  const meridiem = match[3];
  if (!Number.isInteger(hourValue) || !Number.isInteger(minuteValue) || minuteValue < 0 || minuteValue > 59) {
    return DEFAULT_COMMUNICATION_SETTINGS.callSheetDigest.sendTime;
  }

  let hour = hourValue;
  if (meridiem) {
    if (hour < 1 || hour > 12) {
      return DEFAULT_COMMUNICATION_SETTINGS.callSheetDigest.sendTime;
    }
    hour %= 12;
    if (meridiem === 'pm') {
      hour += 12;
    }
  } else if (hour < 0 || hour > 23) {
    return DEFAULT_COMMUNICATION_SETTINGS.callSheetDigest.sendTime;
  }

  return `${hour.toString().padStart(2, '0')}:${minuteValue.toString().padStart(2, '0')}`;
}

function normalizeTimeZone(value: unknown): string {
  if (typeof value !== 'string') {
    return DEFAULT_COMMUNICATION_SETTINGS.timeZone;
  }

  const timeZone = value.trim();
  if (!/^[A-Za-z_]+(?:\/[A-Za-z0-9_+-]+)+$/.test(timeZone)) {
    return DEFAULT_COMMUNICATION_SETTINGS.timeZone;
  }

  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date());
    return timeZone;
  } catch {
    return DEFAULT_COMMUNICATION_SETTINGS.timeZone;
  }
}

function optionalNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

export function normalizeCommunicationSettings(value: unknown): ProjectCommunicationSettings {
  const input = isRecord(value) ? value : {};
  const digestInput = isRecord(input.callSheetDigest) ? input.callSheetDigest : {};
  const digest: CallSheetDigestSettings = {
    enabled: digestInput.enabled === true,
    sendTime: normalizeTime(digestInput.sendTime),
  };

  const lastRunLocalDate = optionalNonEmptyString(digestInput.lastRunLocalDate);
  if (lastRunLocalDate) {
    digest.lastRunLocalDate = lastRunLocalDate;
  }

  const lastRunAt = optionalNonEmptyString(digestInput.lastRunAt);
  if (lastRunAt) {
    digest.lastRunAt = lastRunAt;
  }

  const lastSentAt = optionalNonEmptyString(digestInput.lastSentAt);
  if (lastSentAt) {
    digest.lastSentAt = lastSentAt;
  }

  if (typeof digestInput.lastStatus === 'string' && VALID_DIGEST_STATUSES.has(digestInput.lastStatus as CallSheetDigestStatus)) {
    digest.lastStatus = digestInput.lastStatus as CallSheetDigestStatus;
  }

  return {
    timeZone: normalizeTimeZone(input.timeZone),
    callSheetDigest: digest,
  };
}

export function normalizeEditableCommunicationSettings(value: unknown): ProjectCommunicationSettings {
  const normalized = normalizeCommunicationSettings(value);
  return {
    timeZone: normalized.timeZone,
    callSheetDigest: {
      enabled: normalized.callSheetDigest.enabled,
      sendTime: normalized.callSheetDigest.sendTime,
    },
  };
}
